import React, { useState, useEffect } from "react";
import AddTaskSheet from "./AddTaskSheet";
import CalendarSection from "./CalendarSection";
import { getTodaySchedule, SCHEDULE_DOC_URL } from "../schoolSchedule";
import { getTodaySpecial } from "../kimberlySchedule";
import { loadNoSchoolDays } from "../noSchoolDays";
import { getDayInfo } from "../dayInfo";
import { tomorrowStr } from "../dateUtils";

const PERSON_COLORS = { Kimberly: "#C9847A", Adam: "#7A9E87", Carissa: "#D4A847", Robert: "#6B8EAD" };
const PERSON_EMOJI  = { Kimberly: "💃", Adam: "🏊", Carissa: "👩", Robert: "👨" };
const ACCENT_MUTED  = { Kimberly: "#FBF0EF", Adam: "#EAF2ED", Carissa: "#FBF3E0", Robert: "#D4E0E8" };
const ACCENT_LIGHT  = { Kimberly: "#F2DAD7", Adam: "#C8DDD0", Carissa: "#F0DFA0", Robert: "#B8CCD8" };
const PERSON_CAL_VIEW = { Adam: "adam", Kimberly: "kimberly" };
// Everyone in this list gets a Schedule tab (tasks/reminders + today's events).
const SCHEDULE_TAB_PERSONS = ["Adam", "Kimberly"];
// Both kids now have a documented today's-schedule source (Adam's A–G
// rotation, Kimberly's Mon–Fri specials), so both get the Day/Special
// badge within that tab. Adam additionally gets the full Class Schedule
// periods list below the badge — Kimberly's "schedule" is just the one
// special, already shown in the badge itself.
const TODAY_BADGE_PERSONS = ["Adam", "Kimberly"];

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTodayLabel() {
  return new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatEventTime(event) {
  if (event.isAllDay) return "All day";
  const d = new Date(event.start);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatTaskDate(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const date = new Date(dateStr + "T00:00:00");
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: dateStr, soon: true };
  if (diffDays === 0) return { label: "Due today", soon: true };
  if (diffDays === 1) return { label: "Due tomorrow", soon: true };
  if (diffDays <= 7) return { label: `Due ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`, soon: true };
  return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: false };
}

function DailyScheduleSection({ person, calView, hasTodayBadge, accent, accentMuted }) {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  // getTodaySchedule()/getTodaySpecial() themselves stay synchronous — this
  // just makes sure any snow day added in Notion has been merged in before
  // we first read, and re-renders once that merge lands. If the fetch
  // fails or is still in flight, we fall through to each file's hardcoded
  // dates (see noSchoolDays.js's comment) rather than block on it.
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!hasTodayBadge) return;
    loadNoSchoolDays().then(() => forceUpdate(n => n + 1));
  }, [hasTodayBadge]);
  const schedule = hasTodayBadge && person === "Adam" ? getTodaySchedule() : null;
  const special = hasTodayBadge && person === "Kimberly" ? getTodaySpecial() : null;
  const isSchoolDay = person === "Adam" ? schedule?.isSchoolDay : special?.isSchoolDay;
  const tomorrow = hasTodayBadge ? getDayInfo(person, tomorrowStr()) : null;

  useEffect(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 1);
    fetch(`/.netlify/functions/calendar?view=${calView}&dayStart=${start.toISOString()}&dayEnd=${end.toISOString()}`)
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [calView]);

  useEffect(() => {
    Promise.all([
      fetch(`/.netlify/functions/notion?type=tasks&person=${person}`).then(r => r.json()),
      fetch(`/.netlify/functions/notion?type=reminders&person=${person}`).then(r => r.json()),
    ]).then(([td, rd]) => { setTasks(td.items || []); setReminders(rd.items || []); })
      .catch(() => {})
      .finally(() => setLoadingTasks(false));
  }, [person]);

  const handleCheck = async (e, id, list, setList) => {
    e.stopPropagation();
    setList(prev => prev.filter(i => i.id !== id));
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: id, action: "toggleDone", value: true }) });
  };

  const handleTaskAdded = (item) => {
    if (item.type === "Reminder") setReminders(prev => [item, ...prev]);
    else if (item.type === "To Do") setTasks(prev => [item, ...prev]);
    setShowAddTask(false);
  };

  return (
    <>
      <div style={{ padding: "14px 20px 4px", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setShowAddTask(true)} style={{ background: "var(--sage)", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit" }}>+ Task/Reminder</button>
      </div>
      <div className="section-head"><span className="section-label">Reminders</span></div>
      <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        {loadingTasks ? (
          <div style={{ padding: "16px 20px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading…</div>
        ) : reminders.length === 0 ? (
          <div className="empty-state"><div className="empty-text">No reminders</div></div>
        ) : reminders.map(item => (
          <div key={item.id} className="task-row">
            <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: accent }} />
            </div>
            <div className="task-content">
              <div className="task-name">{item.name}</div>
              {item.dueDate && <div className="task-sub"><span className={`task-date${formatTaskDate(item.dueDate)?.soon ? " soon" : ""}`}>{formatTaskDate(item.dueDate)?.label}</span></div>}
            </div>
          </div>
        ))}
      </div>

      <div className="section-divider" />
      <div className="section-head"><span className="section-label">Tasks</span></div>
      <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        {loadingTasks ? (
          <div style={{ padding: "16px 20px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🎉</div><div className="empty-text">All caught up!</div></div>
        ) : tasks.map(item => {
          const d = formatTaskDate(item.dueDate);
          return (
            <div key={item.id} className="task-row">
              <button className="task-check" onClick={e => handleCheck(e, item.id, tasks, setTasks)} aria-label="Complete task" />
              <div className="task-content">
                <div className="task-name">{item.name}</div>
                {d && <div className="task-sub"><span className={`task-date${d.soon ? " soon" : ""}`}>{d.label}</span></div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="section-divider" />

      {hasTodayBadge && (
        <div style={{ padding: "16px 20px 4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {isSchoolDay ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 20, background: accent, color: "#fff", fontSize: 13, fontWeight: 800 }}>
              {person === "Adam"
                ? <>Day {schedule.letterDay}{schedule.scheduleType !== "regular" && ` · ${schedule.scheduleLabel}`}</>
                : <>{special.special ? special.special.name : "No special today"}{special.halfDay && " · Half Day"}</>}
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 20, background: "var(--border)", color: "var(--muted)", fontSize: 13, fontWeight: 800 }}>
              🎉 No school today
            </span>
          )}
          {person === "Adam" && <a href={SCHEDULE_DOC_URL} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: accent, textDecoration: "none" }}>Full schedule ↗</a>}
          {person === "Kimberly" && isSchoolDay && special.special?.time && <span style={{ fontSize: 12, fontWeight: 700, color: accent }}>{special.special.time}</span>}
        </div>
      )}

      {hasTodayBadge && (
        <div style={{ padding: "2px 20px 14px", display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 }}>Tomorrow</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: tomorrow.label === "No school" ? "var(--muted)" : "var(--mid)" }}>
            {tomorrow.label}{tomorrow.sub && ` · ${tomorrow.sub}`}
          </span>
        </div>
      )}

      <div className="section-head"><span className="section-label">Today's Events</span></div>
      <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        {loadingEvents ? (
          <div style={{ padding: "16px 20px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading…</div>
        ) : events.length === 0 ? (
          <div className="empty-state"><div className="empty-text">Nothing else today</div></div>
        ) : events.map(event => (
          <a key={event.id} href={event.htmlLink || "https://calendar.google.com/calendar/r"} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 20px", borderBottom: "1px solid var(--border)", textDecoration: "none" }}>
            <div style={{ width: 3, borderRadius: 2, alignSelf: "stretch", background: accent, flexShrink: 0, minHeight: 20, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--charcoal)" }}>{event.title}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>{formatEventTime(event)}</div>
              {event.location && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>📍 {event.location}</div>}
            </div>
          </a>
        ))}
      </div>

      {hasTodayBadge && person === "Adam" && schedule.isSchoolDay && (
        <>
          <div className="section-divider" />
          <div className="section-head"><span className="section-label">Class Schedule</span></div>
          <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
            {schedule.periods.map((p, i) => (
              <React.Fragment key={i}>
                {p.lunch && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 20px", borderBottom: "1px solid var(--border)", background: "var(--cream-hover)" }}>
                    <div style={{ width: 26, height: 26, borderRadius: 8, background: "var(--white)", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🍎</div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "var(--charcoal)" }}>Lunch</div>
                    <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, flexShrink: 0 }}>{p.lunch}</div>
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 20px", borderBottom: i < schedule.periods.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: accentMuted, color: accent, fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{p.letter}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--charcoal)" }}>{p.subject || "—"}</div>
                    {(p.teacher || p.room) && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 1 }}>{[p.teacher, p.room].filter(Boolean).join(" · ")}</div>}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, textAlign: "right", flexShrink: 0 }}>{p.start}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </>
      )}
      {showAddTask && (
        <AddTaskSheet
          onClose={() => setShowAddTask(false)}
          onAdd={handleTaskAdded}
          defaultPerson={person}
          lockPerson={true}
        />
      )}
    </>
  );
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
      <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="skeleton skeleton-line-long" style={{ height: 15 }} />
        <div className="skeleton skeleton-line-short" style={{ height: 12 }} />
      </div>
    </div>
  );
}

export default function KidDashboard({ person, onBack, onSchoolTap, onActivityTap, onTabTap }) {
  const scheduleEnabled = SCHEDULE_TAB_PERSONS.includes(person);
  const hasTodayBadge = TODAY_BADGE_PERSONS.includes(person);
  const [tab, setTab] = useState(scheduleEnabled ? "schedule" : "school");
  const [schools, setSchools] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);

  const accent      = PERSON_COLORS[person] || "#7A9E87";
  const accentMuted = ACCENT_MUTED[person]  || "#EAF2ED";
  const accentLight = ACCENT_LIGHT[person]  || "#C8DDD0";
  const emoji       = PERSON_EMOJI[person]  || "👤";
  const calView     = PERSON_CAL_VIEW[person] || null;

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=schools&person=${person}`)
      .then(r => r.json()).then(d => setSchools(d.items || []))
      .finally(() => setLoadingSchools(false));
    fetch(`/.netlify/functions/notion?type=activities&person=${person}`)
      .then(r => r.json()).then(d => setActivities(d.items || []))
      .finally(() => setLoadingActivities(false));
  }, [person]);

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", borderBottom: "1px solid var(--border)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent }} />
          <div style={{ position: "absolute", top: -30, right: -30, width: 110, height: 110, background: accentMuted, borderRadius: "50%", opacity: 0.5 }} />
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 14, padding: "52px 20px 0" }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: accentMuted, border: `2.5px solid ${accentLight}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{emoji}</div>
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.1, position: "relative", display: "inline-block" }}>
                {person}
                <span style={{ position: "absolute", bottom: -2, left: 0, width: "100%", height: 2, background: accent, borderRadius: 2 }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
                <span style={{ fontSize: 14, color: "var(--muted)", fontWeight: 600 }}>Family</span>
                <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--border)" }} />
                <span style={{ fontSize: 13, color: "var(--mid)", fontWeight: 600 }}>{formatTodayLabel()}</span>
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div style={{ display: "flex", marginTop: 8 }}>
            {[
              ...(scheduleEnabled ? [{ id: "schedule", label: "Schedule" }] : []),
              { id: "school", label: "School & Activities" },
              { id: "calendar", label: "Calendar" },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t.id ? accent : "transparent"}`, color: tab === t.id ? accent : "var(--muted)" }}>{t.label}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body">
          {tab === "schedule" && scheduleEnabled && (
            <DailyScheduleSection person={person} calView={calView} hasTodayBadge={hasTodayBadge} accent={accent} accentMuted={accentMuted} />
          )}

          {tab === "school" && (
            <>
              <div className="section-head"><span className="section-label">School</span></div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
                {loadingSchools ? <SkeletonRow /> : schools.length === 0 ? (
                  <div className="empty-state"><div className="empty-text">No active schools</div></div>
                ) : schools.map(school => (
                  <div key={school.id} onClick={() => onSchoolTap(school)} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: "var(--amber-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🏫</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{school.name}</div>
                      <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 2 }}>{school.person}</div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                        {school.startTime && <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--amber-muted)", color: "var(--amber)" }}>{school.startTime}</span>}
                        {school.dismissalTime && <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--amber-muted)", color: "var(--amber)" }}>{school.dismissalTime}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0, paddingTop: 2 }}>
                      {school.officeEmail && <a href={`mailto:${school.officeEmail}`} onClick={e => e.stopPropagation()} style={{ fontSize: 22, textDecoration: "none", lineHeight: 1 }}>✉️</a>}
                      <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="section-divider" />

              <div className="section-head">
                <span className="section-label">Activities</span>
              </div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
                {loadingActivities ? (
                  <><SkeletonRow /><SkeletonRow /></>
                ) : activities.length === 0 ? (
                  <div className="empty-state"><div className="empty-text">No active activities</div></div>
                ) : activities.map(act => (
                  <div key={act.id} onClick={() => onActivityTap(act)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: accentMuted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 }}>🏃</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{act.name}</div>
                      {(act.seasonStart || act.seasonEnd) && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: accent, marginTop: 3 }}>
                          {act.seasonStart && act.seasonEnd ? `${formatDate(act.seasonStart)} – ${formatDate(act.seasonEnd)}` : act.seasonEnd ? `Ends ${formatDate(act.seasonEnd)}` : act.seasonStart ? `Started ${formatDate(act.seasonStart)}` : ""}
                        </div>
                      )}
                      {act.location && <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>📍 {act.location}</div>}
                      {act.schedule && <div style={{ fontSize: 13, color: "var(--mid)", fontWeight: 500, marginTop: 4, lineHeight: 1.5 }}>{act.schedule}</div>}
                    </div>
                    <span style={{ color: "var(--border)", fontSize: 18, flexShrink: 0, marginTop: 2 }}>›</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "calendar" && calView && (
            <CalendarSection view={calView} />
          )}
          {tab === "calendar" && !calView && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 }}>No calendar configured for {person}.</div>
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}
